import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Flex, HStack, Icon, Tooltip, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import DropdownMenu from '../../../../../../../components/DropdownMenu';
import { findSelected } from '../../../../../../../components/DropdownMenu/util';
import FormInput from '../../../../../../../components/form/FormInput';
import { inputTypes } from '../../../../../../../components/form/FormInput/util';
import InputLabel from '../../../../../../../components/form/InputLabel';
import { analyticsIds } from '../../../../../../../constants/analyticsIds';
import { inAppText } from '../../../../../../../constants/inAppText';
import { getValidationObject, validationTypes } from '../../../../../../../constants/regexPatterns';
import {
  adminPageNewUserData,
  selectAdminPageNewUserData,
  selectAdminPageUsers
} from '../../../../../../../redux/modules/adminPage';

const UserInfo = ({ useFormMethods, defaultSelected, isDisabled, step }) => {
  const dispatch = useDispatch();
  const users = useSelector(selectAdminPageUsers);
  const newUserData = useSelector(selectAdminPageNewUserData);

  const userOptions = (users || []).map(({ userFirstName, userLastName, userId }) => ({
    displayName: `${userFirstName} ${userLastName}`,
    id: userId
  }));

  const supervisorOptions = [{ displayName: 'None', id: 0 }, ...userOptions];

  if (step === 0 && newUserData.userFirstName) {
    defaultSelected = newUserData.supervisorId;
  }

  const [selectedSupervisor, setSupervisor] = useState(defaultSelected || null);

  const handleOptionClick = selected => {
    if (Number(selected.value) === 0) {
      setSupervisor(null);
      dispatch(adminPageNewUserData({ ...newUserData, supervisorId: null }));
    } else {
      const supervisor = findSelected(userOptions, Number(selected.value));
      setSupervisor(supervisor.id);
      dispatch(adminPageNewUserData({ ...newUserData, supervisorId: supervisor.id }));
    }
  };

  return (
    <VStack width="100%">
      <HStack justifyContent="space-between" width="inherit">
        <Box width="50%">
          <FormInput
            dataAnalyticsId={analyticsIds.adminSettingsPage.users.createUserForm.firstName}
            inputName="firstName"
            isDisabled={false}
            labelName="First Name"
            placeholder="First Name"
            useFormMethods={useFormMethods}
            validationRules={{ required: { value: true, message: 'First name is required' } }}
          />
        </Box>
        <Box width="50%">
          <FormInput
            dataAnalyticsId={analyticsIds.adminSettingsPage.users.createUserForm.lastName}
            inputName="lastName"
            isDisabled={false}
            labelName="Last Name"
            placeholder="Last Name"
            useFormMethods={useFormMethods}
            validationRules={{ required: { value: true, message: 'Last name is required' } }}
          />
        </Box>
      </HStack>
      <FormInput
        dataAnalyticsId={analyticsIds.adminSettingsPage.users.createUserForm.email}
        inputName="email"
        isDisabled={isDisabled}
        labelName="Email Address"
        placeholder="Email Address"
        toolTip={isDisabled
          && inAppText.tooltips.adminSettings.users.editUser.emailAddress}
        useFormMethods={useFormMethods}
        validationRules={{
          required: { value: true, message: 'Email is required' },
          ...getValidationObject(validationTypes.emailAddress)
        }}
      />
      <HStack width="inherit">
        <Box width="50%">
          <FormInput
            dataAnalyticsId={analyticsIds.adminSettingsPage.users.createUserForm.phone}
            inputName="phone"
            isDisabled={false}
            labelName="Phone Number"
            placeholder="Phone Number"
            required={false}
            type={inputTypes.phoneNumber}
            useFormMethods={useFormMethods}
            validationRules={getValidationObject(validationTypes.phoneNumberPlain)}
          />
        </Box>
        <Box width="50%">
          <FormInput
            dataAnalyticsId={analyticsIds.adminSettingsPage.users.createUserForm.externalId}
            inputName="externalId"
            isDisabled={false}
            labelName="External Id"
            placeholder="External Id"
            required={false}
            useFormMethods={useFormMethods}
          />
        </Box>
      </HStack>
      <Flex direction="column" width="inherit">
        <InputLabel labelName="Supervisor" />
        <DropdownMenu
          dataAnalyticsKey={analyticsIds.adminSettingsPage.users.createUserForm.supervisorDropdown}
          labelKey="displayName"
          options={supervisorOptions}
          selected={selectedSupervisor
            ? findSelected(userOptions, selectedSupervisor).displayName
            : ''}
          width="400px"
          onSelect={handleOptionClick}
        />
      </Flex>
    </VStack>
  );
};

UserInfo.propTypes = {
  register: PropTypes.func,
  errors: PropTypes.shape({}),
  defaultSelected: PropTypes.number,
  watch: PropTypes.func,
  isDisabled: PropTypes.bool,
  step: PropTypes.number,
  control: PropTypes.shape({})
};

export default UserInfo;
